// src/components/common/DataTable/TableFilters.tsx

import {useState, useCallback, useEffect} from "react";
import {Search, Filter, X, Calendar, RotateCcw} from "lucide-react";
import {Input, Select, Button} from "../../ui";
import {clsx} from "clsx";
import {useDebounce} from "../../../hooks";
import type {DataTableFilter} from "./DataTableTypes";

interface TableFiltersProps {
  filters?: DataTableFilter[];
  searchable?: boolean;
  searchPlaceholder?: string;
  onFilter?: (filters: Record<string, unknown>) => void;
  onSearch?: (value: string) => void;
  className?: string;
}

const TableFilters: React.FC<TableFiltersProps> = ({
  filters = [],
  searchable = true,
  searchPlaceholder = "Search...",
  onFilter,
  onSearch,
  className = "",
}) => {
  const [searchValue, setSearchValue] = useState("");
  const [filterValues, setFilterValues] = useState<Record<string, unknown>>({});
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  // Debounce search to avoid too many API calls
  const debouncedSearchValue = useDebounce(searchValue, 300);

  // Call onSearch when debounced value changes
  // Don't include onSearch in dependencies to avoid calling it when the callback reference changes
  useEffect(() => {
    onSearch?.(debouncedSearchValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchValue]);

  // Call onFilter when filter values change
  // Don't include onFilter in dependencies to avoid calling it when the callback reference changes
  useEffect(() => {
    onFilter?.(filterValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValues]);

  const handleSearchChange = useCallback((value: string) => {
    setSearchValue(value);
  }, []);

  const handleFilterChange = useCallback((key: string, value: unknown) => {
    setFilterValues((prev) => ({
      ...prev,
      [key]: value,
    }));
  }, []);

  const handleClearFilter = useCallback((key: string) => {
    setFilterValues((prev) => {
      const newFilters = {...prev};
      delete newFilters[key];
      return newFilters;
    });
  }, []);

  const handleClearAllFilters = useCallback(() => {
    setFilterValues({});
    setSearchValue("");
  }, []);

  const hasActiveFilters =
    Object.keys(filterValues).length > 0 || searchValue.length > 0;
  const hasAdvancedFilters = filters.length > 0;

  const renderFilterInput = (filter: DataTableFilter) => {
    const value = filterValues[filter.key];

    switch (filter.type) {
      case "text":
        return (
          <div key={filter.key} className="relative">
            <Input
              placeholder={filter.placeholder || `Filter by ${filter.label}`}
              value={(value as string) || ""}
              onChange={(e) => handleFilterChange(filter.key, e.target.value)}
              className="pr-8"
            />

            {(value as string | number) && filter.clearable !== false && (
              <button
                onClick={() => handleClearFilter(filter.key)}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                    <X className="w-4 h-4" /> {" "}
              </button>
            )}
          </div>
        );

      case "select":
        return (
          <div key={filter.key} className="relative">
            <Select
              value={value?.toString() || ""}
              onChange={(e) =>
                handleFilterChange(filter.key, e.target.value || undefined)
              }
              options={[
                {value: "", label: `All ${filter.label}`},
                ...(filter.options || []).map((option) => ({
                  value: option.value?.toString() || "",
                  label: option.label,
                })),
              ]}
            />

            {(value as string | number) && filter.clearable !== false && (
              <button
                onClick={() => handleClearFilter(filter.key)}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                    <X className="w-4 h-4" /> {" "}
              </button>
            )}
          </div>
        );

      case "number":
        return (
          <div key={filter.key} className="relative">
            <Input
              type="number"
              placeholder={filter.placeholder || `Filter by ${filter.label}`}
              value={(value as string) || ""}
              onChange={(e) =>
                handleFilterChange(
                  filter.key,
                  e.target.value ? Number(e.target.value) : undefined
                )
              }
              className="pr-8"
            />

            {(value as string | number) && filter.clearable !== false && (
              <button
                onClick={() => handleClearFilter(filter.key)}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                    <X className="w-4 h-4" /> {" "}
              </button>
            )}
          </div>
        );

      case "date":
        return (
          <div key={filter.key} className="relative">
            <Input
              type="date"
              placeholder={filter.placeholder || `Filter by ${filter.label}`}
              value={(value as string) || ""}
              onChange={(e) =>
                handleFilterChange(filter.key, e.target.value || undefined)
              }
              leftIcon={<Calendar className="w-4 h-4" />}
              className="pr-8"
            />

            {(value as string | number) && filter.clearable !== false && (
              <button
                onClick={() => handleClearFilter(filter.key)}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                    <X className="w-4 h-4" /> {" "}
              </button>
            )}
          </div>
        );

      case "dateRange":
        return (
          <div key={filter.key} className="grid grid-cols-2 gap-2">
            <Input
              type="date"
              placeholder="From date"
              value={(value as Record<string, string>)?.from || ""}
              onChange={(e) =>
                handleFilterChange(filter.key, {
                  ...((value as Record<string, string>) || {}),
                  from: e.target.value || undefined,
                })
              }
              leftIcon={<Calendar className="w-4 h-4" />}
            />
            <Input
              type="date"
              placeholder="To date"
              value={(value as Record<string, string>)?.to || ""}
              onChange={(e) =>
                handleFilterChange(filter.key, {
                  ...((value as Record<string, string>) || {}),
                  to: e.target.value || undefined,
                })
              }
              leftIcon={<Calendar className="w-4 h-4" />}
            />
          </div>
        );

      case "numberRange":
        return (
          <div key={filter.key} className="grid grid-cols-2 gap-2">
            <Input
              type="number"
              placeholder="Min"
              value={(value as Record<string, number>)?.min?.toString() || ""}
              onChange={(e) =>
                handleFilterChange(filter.key, {
                  ...((value as Record<string, number>) || {}),
                  min: e.target.value ? Number(e.target.value) : undefined,
                })
              }
            />
            <Input
              type="number"
              placeholder="Max"
              value={(value as Record<string, number>)?.max?.toString() || ""}
              onChange={(e) =>
                handleFilterChange(filter.key, {
                  ...((value as Record<string, number>) || {}),
                  max: e.target.value ? Number(e.target.value) : undefined,
                })
              }
            />
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className={clsx("space-y-4", className)}>
      {/* Search and Filter Toggle Bar */}
      <div className="flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
        {/* Search */}
        {searchable && (
          <div className="flex-1 max-w-md">
            <Input
              placeholder={searchPlaceholder}
              value={searchValue}
              onChange={(e) => handleSearchChange(e.target.value)}
              leftIcon={<Search className="w-4 h-4" />}
              rightIcon={
                searchValue && (
                  <button
                    onClick={() => handleSearchChange("")}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )
              }
            />
          </div>
        )}

        {/* Filter Controls */}
        <div className="flex items-center gap-2">
          {/* Advanced Filters Toggle */}
          {hasAdvancedFilters && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              className={clsx(
                showAdvancedFilters &&
                  "bg-primary-50 border-primary-300 text-primary-700 dark:bg-primary-900/20 dark:border-primary-700 dark:text-primary-300"
              )}
            >
              <Filter className="w-4 h-4 mr-2" />
              Filters
              {Object.keys(filterValues).length > 0 && (
                <span className="ml-2 px-1.5 py-0.5 text-xs bg-primary-100 text-primary-800 dark:bg-primary-800 dark:text-primary-100 rounded-full">
                  {Object.keys(filterValues).length}
                </span>
              )}
            </Button>
          )}

          {/* Clear All Filters */}
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearAllFilters}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Clear
            </Button>
          )}
        </div>
      </div>

      {/* Advanced Filters */}
      {showAdvancedFilters && hasAdvancedFilters && (
        <div className="p-4 bg-gray-50 dark:bg-dark-700 rounded-lg border border-gray-200 dark:border-dark-600">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filters.map((filter) => (
              <div key={filter.key}>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {filter.label}
                </label>
                {renderFilterInput(filter)}
              </div>
            ))}
          </div>

          {/* Active Filters Summary */}
          {Object.keys(filterValues).length > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-dark-600">
              <div className="flex flex-wrap gap-2">
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  Active filters:
                </span>
                {Object.entries(filterValues).map(([key, value]) => {
                  const filter = filters.find((f) => f.key === key);
                  if (!filter || !value) return null;

                  let displayValue: string;
                  if (typeof value === "object" && value !== null) {
                    // Handle range filters
                    const obj = value as Record<string, unknown>;
                    if ("from" in obj && "to" in obj) {
                      displayValue = `${obj.from || ""} - ${obj.to || ""}`;
                    } else if ("min" in obj && "max" in obj) {
                      displayValue = `${obj.min || ""} - ${obj.max || ""}`;
                    } else {
                      displayValue = String(value);
                    }
                  } else {
                    displayValue = String(value);
                  }

                  return (
                    <span
                      key={key}
                      className="inline-flex items-center px-2 py-1 text-xs bg-primary-100 text-primary-800 dark:bg-primary-800 dark:text-primary-100 rounded-full"
                    >
                      {filter.label}: {displayValue}
                      <button
                        onClick={() => handleClearFilter(key)}
                        className="ml-1 text-primary-600 hover:text-primary-800 dark:text-primary-300 dark:hover:text-primary-100"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TableFilters;
